"use client";

import React, { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { PageTransition } from "@/components/ui-enhanced/PageTransition";
import {
  Mail,
  Send,
  AlertCircle,
  Loader2,
  CheckCircle2,
  FileImage,
  X,
  MessageSquare,
  Clock,
  Share2,
  CalendarDays,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import Link from "next/link";
import { motion } from "framer-motion";
import { useMetadata } from "@/hooks/useMetadata";
import PublicLayout from "@/components/PublicLayout";
import { usePageContent } from "@/hooks/usePageContent";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  sanitizeText,
  sanitizeEmail,
  validateEmailForAuth,
} from "@/lib/sanitize";
import PublicHero from "@/components/PublicHero";

const EASE = [0.16, 1, 0.3, 1] as const;

export const CONTACT_COPY = {
  hero: {
    eyebrow: "Support",
    heading: "Report to Admin",
    subtext:
      "Spotted a bug, hit an error, or need to flag something? Send a report straight to the UniUI admin team and we'll look into it.",
  },
  form: {
    fullNameLabel: "Full Name",
    fullNamePlaceholder: "Enter your full name",
    emailLabel: "Email",
    emailPlaceholder: "your.email@example.com",
    subjectLabel: "Report Type",
    subjectPlaceholder: "Select a report type",
    messageLabel: "Details",
    messagePlaceholder:
      "Describe the issue, what happened, and any steps to reproduce it...",
    attachmentLabel: "Attachment",
    attachmentHint:
      "Optional. Screenshots or files help us investigate (max 5 MB).",
    submitButton: "Submit Report",
    submittingButton: "Submitting...",
  },
  infoCards: {
    email: { title: "Email", value: "support@uniui.com.ng" },
    responseTime: {
      title: "Response Time",
      value:
        "Admin reviews reports and typically responds within 24 hours on weekdays.",
    },
    socialMedia: {
      title: "Social Media",
      links: [
        { label: "X / Twitter", href: "https://x.com/1stuniui_com_ng" },
        { label: "TikTok", href: "https://tiktok.com/@1stuniui_com_ng" },
        { label: "GitHub", href: "https://github.com/uniui" },
        { label: "LinkedIn", href: "https://linkedin.com/company/uniui" },
      ],
    },
    officeHours: {
      title: "Office Hours",
      value: "Monday – Friday, 9:00 AM – 5:00 PM WAT",
    },
  },
  faq: {
    heading: "Frequently Reported Issues",
    links: [
      { label: "Password Reset", href: "/faq#password-reset" },
      { label: "Payment Issues", href: "/faq#payment-issues" },
      { label: "Course Not Found", href: "/faq#course-not-found" },
    ],
  },
  success: {
    heading: "Report Submitted",
    subtext:
      "Thank you. Your report has been sent to the UniUI admin team and we'll review it shortly.",
    dashboardLink: "Back to Home",
    dashboardHref: "/",
  },
  subjects: [
    { value: "bug", label: "Report a Bug" },
    { value: "technical", label: "Technical Issue" },
    { value: "course_material", label: "Course Material Issue" },
    { value: "abuse", label: "Report Abuse" },
    { value: "suggestion", label: "Suggestion" },
    { value: "other", label: "Other" },
  ],
};

const contactSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Please enter a valid email address"),
  subject: z.string().min(1, "Please select a subject"),
  message: z
    .string()
    .min(1, "Message is required")
    .max(2000, "Message must be at most 2000 characters"),
});

type ContactFormValues = z.infer<typeof contactSchema>;

export default function ContactPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(
    null,
  );
  const [isDragging, setIsDragging] = useState(false);
  useMetadata({ key: "contact" });
  const copy = usePageContent("contact", CONTACT_COPY);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { fullName: "", email: "", subject: "", message: "" },
  });

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) processFile(files[0]);
  }, []);
  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) processFile(files[0]);
    },
    [],
  );

  const processFile = (file: File) => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File size must be less than 5 MB.");
      return;
    }
    setAttachment(file);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setAttachmentPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };
  const removeAttachment = useCallback(() => {
    setAttachment(null);
    setAttachmentPreview(null);
  }, []);

  const onSubmit = async (data: ContactFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const { apiFetch, versioned } = await import("@/lib/api");

      // Sanitize all inputs before sending
      const sanitizedFullName = sanitizeText(data.fullName);
      const sanitizedEmail = sanitizeEmail(data.email);
      const sanitizedSubject = sanitizeText(data.subject);
      const sanitizedMessage = sanitizeText(data.message);

      // Validate email for temp/disposable domains
      const emailValidation = validateEmailForAuth(sanitizedEmail);
      if (!emailValidation.valid) {
        setError(emailValidation.error ?? null);
        setSubmitting(false);
        return;
      }

      // Backend contract: POST /api/v1/support/contact  { subject, details }
      // We compose a single `details` block so the richer public form (name,
      // email, report_type, attachment note) is preserved for the admin.
      const reportType =
        copy.subjects.find((s) => s.value === sanitizedSubject)?.label ||
        sanitizedSubject;
      const detailLines = [
        `Report type: ${reportType}`,
        `From: ${sanitizedFullName} <${sanitizedEmail}>`,
        attachment
          ? `Attachment: ${attachment.name} (${Math.round(attachment.size / 1024)} KB)`
          : null,
        "",
        sanitizedMessage,
      ].filter(Boolean);

      await apiFetch(versioned("/api/v1/support/contact"), {
        method: "POST",
        body: JSON.stringify({
          subject: `[${reportType}] ${sanitizedFullName}`,
          details: detailLines.join("\n"),
        }),
      });

      toast.success("Message sent successfully!");
      setSubmitted(true);
      reset();
      setAttachment(null);
      setAttachmentPreview(null);
    } catch (err) {
      const status = (err as { status?: number })?.status;
      const message = (err as { message?: string })?.message ?? "";
      setError(
        status === 401
          ? "Please sign in to submit a report, or email us directly."
          : status === 429
            ? "Too many submissions. Please wait before trying again."
            : message.includes("network") || message.includes("fetch")
              ? "Network error. Please check your connection and retry."
              : "Failed to send message. Please try again later.",
      );
      toast.error("Failed to send message.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <PublicLayout>
        <PageTransition>
          <div className="flex min-h-[60vh] items-center justify-center px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="relative w-full max-w-xl text-center"
              >
              <div className="relative z-10">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-y bg-y/10">
                  <CheckCircle2 className="h-10 w-10 text-y" />
                </div>
                <h1 className="display mt-6 text-4xl tracking-tight text-foreground">
                  {copy.success.heading}
                </h1>
                <p className="mx-auto mt-3 max-w-md text-sm font-medium leading-relaxed text-muted-foreground">
                  {copy.success.subtext}
                </p>
                <Link href={copy.success.dashboardHref}>
                  <Button
                    size="lg"
                    className="mt-6 rounded-full bg-y text-black hover:bg-y-light"
                  >
                    {copy.success.dashboardLink}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </PageTransition>
      </PublicLayout>
    );
  }

  const infoCards = [
    {
      icon: Mail,
      title: copy.infoCards.email.title,
      value: copy.infoCards.email.value,
    },
    {
      icon: Clock,
      title: copy.infoCards.responseTime.title,
      value: copy.infoCards.responseTime.value,
    },
    {
      icon: CalendarDays,
      title: copy.infoCards.officeHours.title,
      value: copy.infoCards.officeHours.value,
    },
  ];

  return (
    <PublicLayout>
      <PageTransition>
        <div className="relative overflow-hidden">

          <main className="relative z-10 mx-auto w-full max-w-6xl space-y-16 px-6 py-20 sm:py-28">
            <PublicHero
              eyebrow={copy.hero.eyebrow}
              eyebrowIcon={<MessageSquare size={12} />}
              title={copy.hero.heading}
              subtitle={copy.hero.subtext}
            />

            {/* Split: form + info */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5 lg:items-start">
              {/* Form — double bezel */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="lg:col-span-3"
              >
                <div className="rounded-[2rem] border border-border bg-card p-1.5">
                  <div className="rounded-[calc(2rem-0.375rem)] bg-background p-6 sm:p-8">
                    <form
                      onSubmit={handleSubmit(onSubmit)}
                      className="space-y-5"
                    >
                      <div className="space-y-2">
                        <Label htmlFor="fullName">
                          {copy.form.fullNameLabel}
                        </Label>
                        <Input
                          id="fullName"
                          {...register("fullName")}
                          placeholder={copy.form.fullNamePlaceholder}
                          className="rounded-xl bg-card"
                        />
                        {errors.fullName && (
                          <p className="text-xs font-medium text-destructive">
                            {errors.fullName.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">{copy.form.emailLabel}</Label>
                        <Input
                          id="email"
                          type="email"
                          {...register("email")}
                          placeholder={copy.form.emailPlaceholder}
                          className="rounded-xl bg-card"
                        />
                        {errors.email && (
                          <p className="text-xs font-medium text-destructive">
                            {errors.email.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">
                          {copy.form.subjectLabel}
                        </Label>
                        <Select
                          value={watch("subject") || ""}
                          onValueChange={(val) => setValue("subject", val)}
                        >
                          <SelectTrigger
                            id="subject"
                            className="rounded-xl bg-card"
                          >
                            <SelectValue
                              placeholder={copy.form.subjectPlaceholder}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {copy.subjects.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <input type="hidden" {...register("subject")} />
                        {errors.subject && (
                          <p className="text-xs font-medium text-destructive">
                            {errors.subject.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">
                          {copy.form.messageLabel}
                        </Label>
                        <Textarea
                          id="message"
                          {...register("message")}
                          placeholder={copy.form.messagePlaceholder}
                          rows={6}
                          className="resize-none rounded-xl bg-card"
                          maxLength={2000}
                        />
                        {errors.message && (
                          <p className="text-xs font-medium text-destructive">
                            {errors.message.message}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>{copy.form.attachmentLabel}</Label>
                        <div
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={handleDrop}
                          className={`relative cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                            isDragging
                              ? "border-y bg-y/5"
                              : "border-border bg-card hover:border-y/50"
                          }`}
                        >
                          <input
                            type="file"
                            accept="image/*,.pdf,.txt,.doc,.docx"
                            onChange={handleFileInput}
                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                          />
                          <FileImage className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                          <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                            {copy.form.attachmentHint}
                          </p>
                        </div>

                        {attachmentPreview && (
                          <div className="relative mt-2 inline-block">
                            <img
                              src={attachmentPreview}
                              alt="Attachment preview"
                              className="h-24 w-auto rounded-lg border border-border object-cover"
                              loading="lazy"
                            />
                            <button
                              type="button"
                              onClick={removeAttachment}
                              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-white"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                        {attachment && !attachmentPreview && (
                          <div className="mt-2 flex items-center gap-2 rounded-lg border border-border bg-card p-3">
                            <FileImage className="h-4 w-4 text-y" />
                            <span className="flex-1 truncate text-xs font-bold">
                              {attachment.name}
                            </span>
                            <button
                              type="button"
                              onClick={removeAttachment}
                              className="text-muted-foreground transition-colors hover:text-destructive"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>

                      {error && (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/10 p-3">
                            <AlertCircle className="h-4 w-4 text-destructive" />
                            <p className="text-xs font-bold text-destructive">
                              {error}
                            </p>
                          </div>
                          <p className="text-center text-xs text-muted-foreground">
                            If_this_keeps_happening, email us directly at{" "}
                            <a
                              href="mailto:support@uniui.com.ng"
                              className="font-bold text-y hover:underline"
                            >
                              support@uniui.com.ng
                            </a>
                            .
                          </p>
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={submitting}
                        size="lg"
                        className="w-full rounded-full bg-y text-black hover:bg-y-light"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {copy.form.submittingButton}
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            {copy.form.submitButton}
                          </>
                        )}
                      </Button>
                    </form>
                  </div>
                </div>
              </motion.div>

              {/* Info cards */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
                className="space-y-4 lg:col-span-2"
              >
                {infoCards.map((card) => (
                  <div key={card.title} className="matte-metal rounded-2xl p-5">
                    <div className="mb-2 flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-y/10 text-y">
                        <card.icon className="h-5 w-5" />
                      </div>
                      <h3 className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">
                        {card.title}
                      </h3>
                    </div>
                    <p className="text-sm font-bold leading-relaxed text-foreground">
                      {card.value}
                    </p>
                  </div>
                ))}

                <div className="matte-metal rounded-2xl p-5">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-y/10 text-y">
                      <Share2 className="h-5 w-5" />
                    </div>
                    <h3 className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">
                      {copy.infoCards.socialMedia.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {copy.infoCards.socialMedia.links.map((link) => (
                      <li key={link.label}>
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-bold transition-colors hover:text-y inline-flex items-center gap-1"
                        >
                          {link.label}
                          <ArrowUpRight size={12} className="opacity-40" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="matte-metal rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-y shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-[0.18em] text-y">
                        Response SLA
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        We typically reply within 24 hours. For critical system
                        exploits or data privacy concerns, please email{" "}
                        <a
                          href="mailto:support@uniui.com.ng"
                          className="text-y hover:underline font-bold"
                        >
                          support@uniui.com.ng
                        </a>
                        .
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* FAQ */}
            <section className="mx-auto max-w-3xl space-y-6">
              <h2 className="text-center text-xs font-black uppercase tracking-[0.22em] text-y">
                {copy.faq.heading}
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {copy.faq.links.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    className="group flex items-center justify-between rounded-2xl p-5 matte-metal"
                  >
                    <span className="text-xs font-black uppercase tracking-[0.14em] text-muted-foreground transition-colors group-hover:text-foreground">
                      {link.label}
                    </span>
                    <span className="text-y">→</span>
                  </Link>
                ))}
              </div>
            </section>
          </main>
        </div>
      </PageTransition>
    </PublicLayout>
  );
}